import { useEffect, useRef } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { Barbell, Fire, Lightning, Medal, Trophy, X } from "@phosphor-icons/react";
import { MilestoneThumb } from "./MilestoneThumb";

const ICON_MAP = { Barbell, Fire, Lightning, Medal, Trophy };

const ACCENTS = {
  workouts: "#007AFF",
  streak: "#FF9500",
  phase: "#D4FF00",
};

function formatDate(iso) {
  if (!iso) return "";
  try {
    return new Date(iso).toLocaleDateString("it-IT", {
      day: "2-digit", month: "long", year: "numeric",
    });
  } catch {
    return "";
  }
}

/**
 * Preview modal of a single badge — opened by tapping the badge card's
 * thumbnail slot. Frontend-only.
 *
 * Accessibility:
 * - Renders a real <dialog>-style overlay (`role="dialog"`, `aria-modal`).
 * - Closes on Escape, on backdrop click, on close-button click.
 * - When closed, focus is restored on the trigger via `returnFocusRef`.
 * - Background scroll is locked while open.
 */
export function BadgePreviewModal({ badge, shareToken, onClose, returnFocusRef }) {
  const closeBtnRef = useRef(null);

  // Lock background scroll + restore focus on close.
  useEffect(() => {
    if (!badge) return undefined;
    const prevOverflow = document.body.style.overflow;
    document.body.style.overflow = "hidden";
    // Move keyboard focus to the close button when the dialog opens.
    closeBtnRef.current?.focus?.();
    const handleKey = (e) => {
      if (e.key === "Escape") onClose?.();
    };
    window.addEventListener("keydown", handleKey);
    return () => {
      document.body.style.overflow = prevOverflow;
      window.removeEventListener("keydown", handleKey);
      // Return focus to the trigger that opened the dialog.
      returnFocusRef?.current?.focus?.();
    };
  }, [badge, onClose, returnFocusRef]);

  if (!badge) return null;
  const Icon = ICON_MAP[badge.icon] || Trophy;
  const accent = ACCENTS[badge.category] || "#D4FF00";
  const hasThumb = !!badge.thumbnail_path;

  return (
    <AnimatePresence>
      <motion.div
        key={`preview-${badge.code}`}
        className="fixed inset-0 z-[80] flex items-center justify-center px-4"
        role="dialog"
        aria-modal="true"
        aria-labelledby={`badge-preview-title-${badge.code}`}
        data-testid="badge-preview-modal"
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        exit={{ opacity: 0 }}
        transition={{ duration: 0.18 }}
      >
        {/* Backdrop — closes on click */}
        <button
          type="button"
          aria-label="Chiudi anteprima"
          onClick={onClose}
          className="absolute inset-0 bg-black/80 backdrop-blur-sm cursor-default"
          data-testid="badge-preview-backdrop"
        />

        <motion.div
          initial={{ scale: 0.88, y: 24 }}
          animate={{ scale: 1, y: 0 }}
          exit={{ scale: 0.92, y: 12 }}
          transition={{ type: "spring", stiffness: 320, damping: 26 }}
          className="relative w-full max-w-sm bg-[#0F0F0F] border rounded-3xl p-6 shadow-2xl"
          style={{ borderColor: `${accent}44` }}
        >
          {/* Close button (top-right) */}
          <button
            ref={closeBtnRef}
            type="button"
            onClick={onClose}
            className="absolute top-3 right-3 w-9 h-9 rounded-full bg-[#1A1A1A] hover:bg-[#262626] text-zinc-400 hover:text-white flex items-center justify-center transition-colors outline-none focus-visible:ring-2 focus-visible:ring-white/30"
            aria-label="Chiudi"
            data-testid="badge-preview-close"
          >
            <X size={14} weight="bold" />
          </button>

          <p
            className="text-[10px] uppercase tracking-[0.18em] font-bold text-center"
            style={{ color: accent }}
          >
            {badge.unlocked ? "Milestone sbloccata" : "Milestone bloccata"}
          </p>

          {/* Hero image / icon */}
          <div className="my-5 flex items-center justify-center">
            <div
              className="w-44 h-44 flex items-center justify-center"
              style={hasThumb ? { filter: `drop-shadow(0 0 32px ${accent}55)` } : null}
            >
              {hasThumb ? (
                <MilestoneThumb
                  path={badge.thumbnail_path}
                  shareToken={shareToken}
                  alt={badge.name}
                  className="max-w-full max-h-full w-full h-full object-contain object-center"
                />
              ) : (
                <div
                  className="w-32 h-32 rounded-3xl flex items-center justify-center"
                  style={{
                    backgroundColor: `${accent}1f`,
                    border: `1px solid ${accent}55`,
                    boxShadow: `0 0 40px ${accent}33`,
                  }}
                >
                  <Icon size={64} weight="fill" style={{ color: accent }} />
                </div>
              )}
            </div>
          </div>

          <h2
            id={`badge-preview-title-${badge.code}`}
            className="text-2xl font-black tracking-tight text-white text-center uppercase"
            style={{ fontFamily: "Barlow Condensed, sans-serif", letterSpacing: "0.02em" }}
          >
            {badge.name}
          </h2>

          {badge.description && (
            <p className="text-sm text-zinc-400 text-center mt-1.5">{badge.description}</p>
          )}

          {badge.unlocked && badge.unlocked_at && (
            <p className="text-[11px] text-zinc-500 text-center mt-3">
              Sbloccato il <span className="text-zinc-300 font-medium">{formatDate(badge.unlocked_at)}</span>
            </p>
          )}
        </motion.div>
      </motion.div>
    </AnimatePresence>
  );
}
